/**
 * Token refresh middleware — intercepts 401/400/403 errors and attempts
 * to refresh the API token before retrying the original request.
 */
export function createTokenRefreshMiddleware({
  refreshFn,
  onLogout,
  isMobileApp,
  getPrivateKey,
  getPartnerClientId,
  baseURL
}) {
  let refreshPromise = null;

  const skipped = [
    '/v2/token',
    '/v2/authorization',
    '/v3/fe/web-user',
    '/v2/authorization/logout',
    '/v2/authorization/validate-sms-code',
    '/v3/fe/web-users/change-password',
    '/v3/fe/questions',
    '/v3/fe/appointments',
    '/v3/fe/web-users/resend-2fa-code',
    '/v2/authorization/third-party',
    '/v3/fe/web-user/benefit/authorization'
  ];

  const skippedSubstr = ['/v3/fe/questions/', '/v3/fe/appointments/'];

  return next =>
    async (url, init = {}) => {
      try {
        return await next(url, init);
      } catch (error) {
        // If error is not HttpError-like (no .status), propagate
        if (!error || typeof error.status !== 'number') {
          throw error;
        }

        const { status } = error;

        // Extract path from URL — url is already a full URL from httpClient
        // Strip the baseURL pathname prefix (e.g. '/api' → '/v2/token')
        const parsedUrl = new URL(url);
        const basePath = new URL(baseURL).pathname.replace(/\/$/, '');
        const path = parsedUrl.pathname.slice(basePath.length);

        // Special logout triggers — always reject after logout
        if (
          ((path === '/v2/token' || path === '/v2/authorization/refresh-access-token') &&
            status === 401) ||
          (path === '/v3/fe/web-user' && status === 401)
        ) {
          onLogout();
          throw error;
        }

        // Whitelist — skip refresh, reject immediately
        if (skipped.includes(path) || skippedSubstr.some(substr => path.includes(substr))) {
          throw error;
        }

        // Check if this is a refresh-triggering condition
        const shouldRefresh =
          (status === 400 && error.data?.message === 'Missing required parameter in header') ||
          status === 401 ||
          (status === 403 && (!error.data?.error || error.data?.error === 'Invalid api token'));

        if (!shouldRefresh) {
          throw error;
        }

        // Prevent infinite loop — don't retry if already a retry
        if (init.__isRetryRequest) {
          throw error;
        }

        // Deduplicated refresh
        if (!refreshPromise) {
          const isMobile = isMobileApp();
          const partnerHash = getPrivateKey(isMobile);
          const partnerClientId = getPartnerClientId(isMobile);

          refreshPromise = refreshFn({
            partnerHash,
            partnerId: 0,
            partnerClientId
          }).finally(() => {
            refreshPromise = null;
          });
        }

        await refreshPromise;

        // Retry original request
        return next(url, { ...init, __isRetryRequest: true });
      }
    };
}
